import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { loginRedirect } from "@/components/Layout";

const input = "w-full border border-stone-200 rounded-xl px-3 py-2 text-sm";

async function chunkedUpload(file, meta, onProgress) {
  const init = await api.post("/ott/uploads", { ...meta, size_bytes: file.size, mime: file.type || "video/mp4" });
  const { upload_id, chunk_bytes } = init.data;
  for (let off = 0; off < file.size; off += chunk_bytes) {
    const part = file.slice(off, off + chunk_bytes);
    await api.put(`/ott/uploads/${upload_id}/chunk`, part, { params: { offset: off }, headers: { "Content-Type": "application/octet-stream" } });
    onProgress(Math.min(100, Math.round(((off + part.size) / file.size) * 100)));
  }
  return (await api.post(`/ott/uploads/${upload_id}/complete`)).data;
}

export default function OttUpload() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [cats, setCats] = useState([]);
  const [mode, setMode] = useState("file");
  const [m, setM] = useState({ title: "", description: "", category: "", rights_confirmed: false, url: "" });
  const [file, setFile] = useState(null);
  const [pct, setPct] = useState(0);
  const [busy, setBusy] = useState(false);
  const [rules, setRules] = useState({ rules_enforced: false, categories: [] });
  const [providers, setProviders] = useState([]);
  useEffect(() => {
    api.get("/ott/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/ott/categories", { params: { detail: 1 } }).then((r) => setRules(r.data)).catch(() => {});
    api.get("/ott/providers").then((r) => setProviders(r.data)).catch(() => {});
  }, []);
  const rule = (k) => rules.categories.find((c) => c.key === k);
  const blocked = (k) => rules.rules_enforced && rule(k) && !rule(k)[mode === "file" ? "uploads_allowed" : "links_allowed"];
  if (!user) return <div className="max-w-md mx-auto py-16 text-center"><button onClick={loginRedirect} className="rounded-full bg-red-600 text-white font-bold px-5 py-2" data-testid="ott-login">Sign in to publish</button></div>;
  const set = (k) => (e) => setM({ ...m, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async () => {
    setBusy(true); setPct(0);
    try {
      const { url, ...meta } = m;
      const v = mode === "file" ? await chunkedUpload(file, meta, setPct) : (await api.post("/ott/videos/link", m)).data;
      toast.success(v.status === "published" ? "Published" : "Submitted — a moderator will review it");
      nav(`/ott/watch/${v.video_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };
  const ready = m.title.trim().length >= 3 && m.category && m.rights_confirmed && (mode === "file" ? !!file : m.url.trim());
  return (
    <div className="max-w-xl mx-auto px-4 py-6 space-y-3" data-testid="ott-upload-page">
      <h1 className="heading text-2xl font-black text-stone-900">Publish a video</h1>
      <p className="text-xs text-stone-500">New creators are reviewed by a moderator before going live; trusted creators publish after automatic checks. MP4/MOV (H.264) or WebM, limited size and length.</p>
      <div className="inline-flex bg-stone-100 rounded-full p-1">
        {[["file", "Upload file"], ["link", "Video link"]].map(([k, l]) => (
          <button key={k} onClick={() => setMode(k)} className={`text-xs font-bold px-4 py-1.5 rounded-full ${mode === k ? "bg-white shadow-sm" : "text-stone-500"}`} data-testid={`ott-mode-${k}`}>{l}</button>
        ))}
      </div>
      {mode === "file"
        ? <input type="file" accept="video/mp4,video/quicktime,video/webm" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="ott-file" />
        : <div className="space-y-1">
            <input value={m.url} onChange={set("url")} placeholder="https://www.youtube.com/watch?v=…" className={input} data-testid="ott-url" />
            <p className="text-[11px] text-stone-500" data-testid="ott-link-hint">Accepted: {providers.filter((p) => p.mode !== "link_out").map((p) => p.label).join(", ") || "YouTube, Vimeo"}{providers.some((p) => p.mode === "link_out") ? ` + ${providers.filter((p) => p.mode === "link_out").length} streaming apps (link only, opens in their app)` : ""}. Shared links are reviewed before going live.</p>
          </div>}
      <input value={m.title} onChange={set("title")} placeholder="Title" className={input} data-testid="ott-title" />
      <textarea value={m.description} onChange={set("description")} placeholder="Description (optional)" rows={3} className={input} data-testid="ott-description" />
      <select value={m.category} onChange={set("category")} className={input} data-testid="ott-category">
        <option value="">Choose a category</option>
        {cats.map((c) => <option key={c} value={c} disabled={blocked(c)}>{rule(c)?.label || c.replace("-", " ")}{rule(c)?.review === "always" ? " · reviewed" : ""}</option>)}
      </select>
      <label className="text-xs text-stone-600 flex gap-2"><input type="checkbox" checked={m.rights_confirmed} onChange={set("rights_confirmed")} data-testid="ott-rights" /> I own this video or have permission to publish it.</label>
      {busy && mode === "file" && <div className="h-2 bg-stone-100 rounded-full overflow-hidden" data-testid="ott-progress"><div className="h-full bg-red-600 transition-[width]" style={{ width: `${pct}%` }} /></div>}
      <button onClick={submit} disabled={!ready || busy} className="w-full rounded-full bg-red-600 disabled:opacity-40 text-white font-bold py-2.5" data-testid="ott-submit">{busy ? (mode === "file" ? `Uploading ${pct}%` : "Checking…") : "Publish"}</button>
    </div>
  );
}
